// src/pages/manager/Post.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  FormControl,
  Grid,
  Input,
  InputLabel,
} from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { useBmapi } from "../../utils/bmapi-context";
import { useIntl } from "react-intl";
import { common, form, navigation } from "../../messages";
import Title from "../../ui/Title";
import { getErrorMessageString } from "../../utils/errors";
import { MANAGER_ROUTES } from "../../utils/constants";
import { ArrowBack, ArrowForward } from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";

function Navigation() {
  const intl = useIntl();

  return (
    <FormControl margin="normal" fullWidth>
      <Grid
        container
        direction="row"
        justify="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Grid item>
          <Button
            component={RouterLink}
            to={MANAGER_ROUTES.MANAGE_CAMPUS_WEB_BUILDER}
            startIcon={<ArrowBack />}
          >
            {intl.formatMessage(common.back)}
          </Button>
        </Grid>
        <Grid item>
          <Button
            component={RouterLink}
            to={MANAGER_ROUTES.HOME}
            startIcon={<ArrowForward />}
          >
            {intl.formatMessage(navigation.goHome)}
          </Button>
        </Grid>
      </Grid>
    </FormControl>
  );
}

export default function WebPage() {
  const initialValues = useMemo(
    () => ({
      title: "",
      slug: "",
      html_content: "",
      css_content: "",
    }),
    []
  );

  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const { bmapi, notifyError, notifySuccess } = useBmapi();

  const [dirty, setDirty] = useState(null);
  const [values, setValues] = useState(initialValues);

  const searchParams = new URLSearchParams(location.search);
  const pageId = searchParams.get("id");

  useEffect(() => {
    loadPage().catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [loadPage, notifyError, intl]);

  const handlePage = (event) => {
    event.preventDefault();
    setDirty(false);

    const pageFnc = pageId
      ? bmapi.updateWebPage(values)
      : bmapi.createNewWebPage(values);

    const successMessage = pageId
      ? intl.formatMessage({
          id: "pages.webPage.updated",
          defaultMessage: "Pagina aggiornata con successo",
        })
      : intl.formatMessage({
          id: "pages.webPage.saved",
          defaultMessage: "Pagina creata con successo",
        });

    pageFnc
      .then(() => {
        notifySuccess(successMessage);
        history.push(MANAGER_ROUTES.MANAGE_CAMPUS_WEB_BUILDER);
      })
      .catch((e) => {
        notifyError(getErrorMessageString(e, intl));
      });
  };

  const loadPage = useCallback(async () => {
    if (bmapi && pageId) {
      // MODIFICA pagina esistente
      const resp = await bmapi.getWebPage(pageId);
      setValues(resp || {});
      setDirty(false);
    } else {
      // NUOVA pagina vuota
      setValues(initialValues);
      setDirty(false);
    }
  }, [bmapi, pageId, initialValues]);

  const handleValue = useCallback(
    (varName) => (e) => {
      setDirty(true);
      ((val) => setValues((v) => ({ ...v, [varName]: val })))(e.target.value);
    },
    []
  );

  dirty;

  return (
    <Container maxWidth="sm">
      <Title>{pageId ? "Modifica pagina" : "Nuova pagina"}</Title>
      <Box mb={2}>
        <form onSubmit={handlePage}>
          <Card>
            <CardContent>
              <FormControl fullWidth required margin="dense">
                <InputLabel>{intl.formatMessage(form.title)}</InputLabel>
                <Input
                  type="text"
                  value={values.title}
                  name="title"
                  onChange={handleValue("title")}
                />
              </FormControl>
              <FormControl fullWidth required margin="dense">
                <InputLabel>{intl.formatMessage(form.slug)}</InputLabel>
                <Input
                  type="text"
                  value={values.slug}
                  name="slug"
                  onChange={handleValue("slug")}
                />
              </FormControl>
              <FormControl fullWidth margin="dense">
                <InputLabel>{intl.formatMessage(form.htmlContent)}</InputLabel>
                <Input
                  type="text"
                  value={values.html_content}
                  name="html_content"
                  onChange={handleValue("html_content")}
                />
              </FormControl>
              <FormControl fullWidth margin="dense">
                <InputLabel>{intl.formatMessage(form.cssContent)}</InputLabel>
                <Input
                  type="text"
                  value={values.css_content}
                  name="css_content"
                  onChange={handleValue("css_content")}
                />
              </FormControl>
            </CardContent>
          </Card>
          <Box mt={2}>
            <CardActions>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                {pageId
                  ? intl.formatMessage(common.save)
                  : intl.formatMessage(common.create)}
              </Button>
            </CardActions>
          </Box>
        </form>
        <Navigation />
      </Box>
    </Container>
  );
}
